import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Truck, Clock, Building2, CheckCircle2, Route as RouteIcon } from "lucide-react";
import { CITIES } from "../data/cities";
import { ROUTES, ROUTE_SLUG } from "../data/routes";
import PriceCalculatorHero from "../components/PriceCalculatorHero";
import useSeo from "../hooks/useSeo";

/**
 * RoutePage — landing page for "kurer fra X til Y" search intent.
 * Each instance receives fromSlug + toSlug props (set via the Route element).
 */
export default function RoutePage({ fromSlug, toSlug }) {
  const from = CITIES[fromSlug];
  const to = CITIES[toSlug];
  const route = ROUTES.find((r) => r.fromSlug === fromSlug && r.toSlug === toSlug);

  useSeo({
    title: from && to ? `Kurer fra ${from.name} til ${to.name} · Fra ${route?.priceSameDay} kr · EMA Kurer` : "EMA Kurer",
    description: from && to
      ? `Direkte kurérservice fra ${from.name} til ${to.name}. ~${route?.approxKm} km. Same-day fra ${route?.priceSameDay} kr inkl. moms. Book online, betal sikkert, følg pakken live.`
      : "EMA Kurer ApS — direkte kurérservice på Sjælland.",
    path: from && to ? ROUTE_SLUG(fromSlug, toSlug) : "/",
  });

  if (!from || !to || !route) return <Navigate to="/" replace />;

  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Kurer fra ${from.name} til ${to.name}`,
    description: `Direkte kurérservice fra ${from.name} til ${to.name}. Same-day, express og aftenlevering.`,
    provider: { "@id": "https://emakurer.dk/#business" },
    areaServed: [
      { "@type": "City", name: from.nameLong, addressCountry: "DK" },
      { "@type": "City", name: to.nameLong, addressCountry: "DK" },
    ],
    serviceType: "Kurérservice",
    offers: {
      "@type": "Offer",
      price: String(route.priceSameDay),
      priceCurrency: "DKK",
      description: `Same-day kurér fra ${from.name} til ${to.name} (~${route.approxKm} km).`,
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: `Hvad koster kurer fra ${from.name} til ${to.name}?`, acceptedAnswer: { "@type": "Answer", text: `Same-Day kurér fra ${from.name} til ${to.name} koster ${route.priceSameDay} kr inkl. moms ved ~${route.approxKm} km. Prisen består af 199 kr startpris (inkl. 8 km) + ${Math.max(0, route.approxKm - 8)} km × 6 kr/km. Tillæg ved weekend (+20%) og sen aften (+15%).` } },
      { "@type": "Question", name: `Hvor lang tid tager kurer fra ${from.name} til ${to.name}?`, acceptedAnswer: { "@type": "Answer", text: `Køretiden er typisk 35-60 minutter direkte fra A til B. Ingen lager, ingen omlastning, ingen ventetid. Same-day levering samme dag, Express indenfor 1-2 timer efter booking.` } },
      { "@type": "Question", name: `Kan I afhente om aftenen fra ${from.name}?`, acceptedAnswer: { "@type": "Answer", text: `Ja. Aftenlevering kører 18:00-22:00. Pris fra ${route.priceSameDay + 40} kr afhængig af tidspunkt.` } },
    ],
  };

  return (
    <div data-testid={`route-page-${fromSlug}-${toSlug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* HERO */}
      <section className="bg-brand-dark text-white pt-16 pb-12 lg:pt-20 lg:pb-16" data-testid="route-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 text-white/80 bg-white/5 border border-white/10 backdrop-blur-md px-4 py-2" style={{ color: "#fca5a5" }}>
            <RouteIcon size={14} />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold">Rute · {from.name} → {to.name} · ~{route.approxKm} km</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05]">
            Kurer fra {from.name}<br/>
            <span className="text-brand-red">til {to.name}.</span>
          </h1>
          <p className="text-white/80 text-lg max-w-3xl mt-6 leading-relaxed">
            Direkte kurérservice fra <strong className="text-white">{from.name}</strong> til <strong className="text-white">{to.name}</strong>. Vi henter pakken direkte hos dig og kører den direkte til modtageren — ingen lager, ingen omlastning. <strong className="text-white">Same-Day fra {route.priceSameDay} kr</strong> inkl. moms.
          </p>

          <div className="mt-10 max-w-4xl">
            <PriceCalculatorHero />
          </div>
        </div>
      </section>

      {/* PRICE BREAKDOWN */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20" data-testid="route-pricing">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="kicker">Prisstruktur</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl mt-3 text-brand-dark tracking-tight">
              Sådan beregner vi prisen
            </h2>
            <p className="text-brand-text2 mt-5 text-lg leading-relaxed">
              Ruten fra {from.name} til {to.name} er ca. <strong>{route.approxKm} km</strong>. Vi beregner prisen transparent: startpris 199 kr inkl. de første 8 km, plus 6 kr/km for ekstra distance.
            </p>

            <div className="mt-8 bg-white border border-brand p-6 max-w-2xl">
              <div className="text-xs uppercase tracking-widest text-brand-text2">Eksempel — Same-Day</div>
              <div className="font-display font-semibold text-xl text-brand-dark mt-2">{from.name} → {to.name} · {route.approxKm} km</div>
              <table className="w-full mt-5 text-sm">
                <tbody>
                  <tr className="border-b border-slate-100"><td className="py-2 text-brand-text2">Startpris (inkl. 8 km)</td><td className="text-right font-semibold text-brand-dark">199 kr</td></tr>
                  <tr className="border-b border-slate-100"><td className="py-2 text-brand-text2">Ekstra distance: {Math.max(0, route.approxKm - 8)} km × 6 kr</td><td className="text-right font-semibold text-brand-dark">{Math.max(0, route.approxKm - 8) * 6} kr</td></tr>
                  <tr className="border-t-2 border-brand-dark"><td className="py-3 font-display font-bold text-brand-dark">Total inkl. moms</td><td className="text-right font-display font-bold text-brand-red text-xl">{route.priceSameDay} kr</td></tr>
                </tbody>
              </table>
              <p className="text-xs text-brand-text2 mt-3">Tillæg: weekend (+20%), sen aften efter kl. 20 (+15%). Express er ~45% dyrere men hurtigere.</p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white border border-brand p-4"><Truck size={18} className="text-brand-red" /><div className="font-display font-semibold text-brand-dark mt-2 text-sm">Same-Day</div><div className="text-brand-red font-bold text-lg mt-1">{route.priceSameDay} kr</div></div>
              <div className="bg-white border border-brand p-4"><Clock size={18} className="text-brand-red" /><div className="font-display font-semibold text-brand-dark mt-2 text-sm">Express</div><div className="text-brand-red font-bold text-lg mt-1">{Math.round(route.priceSameDay * 1.45)} kr</div></div>
              <div className="bg-white border border-brand p-4"><Clock size={18} className="text-brand-red" /><div className="font-display font-semibold text-brand-dark mt-2 text-sm">Aftenlevering</div><div className="text-brand-red font-bold text-lg mt-1">{route.priceSameDay + 40} kr</div></div>
            </div>
          </div>

          {/* Sidebar — both cities + business CTA */}
          <aside className="bg-brand-bg2 border border-brand p-7 self-start" data-testid="route-sidebar">
            <div className="kicker">Læs mere om byerne</div>
            <div className="space-y-3 mt-4">
              <Link to={`/kurer-${from.slug}`} className="block bg-white border border-brand p-4 hover:border-brand-red transition-colors group">
                <MapPin size={16} className="text-brand-red" />
                <div className="font-display font-semibold text-brand-dark mt-2 group-hover:text-brand-red transition-colors">Kurer i {from.name}</div>
                <div className="text-xs text-brand-text2 mt-1">Lokale priser + dækningsområde</div>
              </Link>
              <Link to={`/kurer-${to.slug}`} className="block bg-white border border-brand p-4 hover:border-brand-red transition-colors group">
                <MapPin size={16} className="text-brand-red" />
                <div className="font-display font-semibold text-brand-dark mt-2 group-hover:text-brand-red transition-colors">Kurer i {to.name}</div>
                <div className="text-xs text-brand-text2 mt-1">Lokale priser + dækningsområde</div>
              </Link>
            </div>

            <div className="mt-6 border-t border-brand pt-5">
              <Building2 size={18} className="text-brand-red" />
              <div className="font-display font-semibold text-brand-dark mt-2">Faste {from.name}↔{to.name} kørsler?</div>
              <p className="text-sm text-brand-text2 mt-2">Hvis du har behov for ugentlige eller daglige leveringer på denne rute, kan vi lave en fast aftale med månedsfaktura.</p>
              <Link to="/erhverv" className="btn-secondary w-full justify-center mt-4" data-testid="route-cta-business">
                Se erhvervsaftaler
              </Link>
            </div>

            <Link to="/book" className="btn-primary w-full justify-center mt-4" data-testid="route-cta-book">
              Book {from.name} → {to.name} <ArrowRight size={14} />
            </Link>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-bg2 border-y border-brand py-16 lg:py-20" data-testid="route-faq">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker">Ofte stillede spørgsmål</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl mt-3 text-brand-dark tracking-tight">
            Kurer {from.name} til {to.name} — FAQ
          </h2>
          <div className="mt-10 space-y-4">
            {faqLd.mainEntity.map((f, i) => (
              <details key={i} className="bg-white border border-brand p-5 group" data-testid={`route-faq-${i}`}>
                <summary className="font-display font-semibold text-brand-dark cursor-pointer flex items-center justify-between gap-3 list-none">
                  <span>{f.name}</span>
                  <span className="text-brand-red text-2xl font-light group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-brand-text2 mt-4 leading-relaxed">{f.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER ROUTES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20" data-testid="route-other">
        <div className="kicker">Populære ruter</div>
        <h2 className="font-display font-semibold text-2xl sm:text-3xl mt-3 text-brand-dark tracking-tight">
          Andre kurérruter på Sjælland
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {ROUTES.filter((r) => !(r.fromSlug === fromSlug && r.toSlug === toSlug)).slice(0, 8).map((r) => {
            const f = CITIES[r.fromSlug];
            const t = CITIES[r.toSlug];
            if (!f || !t) return null;
            return (
              <Link
                key={`${r.fromSlug}-${r.toSlug}`}
                to={ROUTE_SLUG(r.fromSlug, r.toSlug)}
                className="group bg-white border border-brand p-4 hover:border-brand-red transition-colors"
                data-testid={`route-link-${r.fromSlug}-${r.toSlug}`}
              >
                <CheckCircle2 size={16} className="text-brand-red" />
                <div className="font-display font-semibold text-brand-dark mt-2 text-sm group-hover:text-brand-red transition-colors">{f.name} → {t.name}</div>
                <div className="text-xs text-brand-text2 mt-1">Fra {r.priceSameDay} kr · {r.approxKm} km</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-dark text-white py-16 lg:py-20" data-testid="route-final-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <div className="kicker text-brand-red">Klar?</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight">
            Book {from.name} → {to.name} nu.
          </h2>
          <p className="text-white/70 mt-5">Online booking på 2 minutter. Sikker betaling.</p>
          <Link to="/book" className="btn-primary mt-8" data-testid="route-final-book">
            Book afhentning <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
